/**
 * Address picker rollback escape hatch.
 *
 * Phase 2 (2026-05-08): v2 is now the default for everyone. This module
 * answers the inverse question: "should we render the legacy AddressLookup
 * instead?" — and returns `true` only when the user has explicitly opted into
 * the legacy picker.
 *
 * Two opt-OUT mechanisms (either is sufficient):
 *   1. URL query parameter:  ?address-picker=legacy
 *   2. localStorage flag:    address_picker_v2 = "0"
 *
 * The query param also persists to localStorage so the rollback survives the
 * route change off the home page (where the picker isn't rendered) into the
 * Collection-Delivery / Removals routes (where it is). Setting
 * `?address-picker=v2` writes "1" to localStorage which is a no-op for the
 * default v2 case but cleanly cancels a previous legacy override — useful for
 * QA toggling between sides without clearing storage manually.
 *
 * Default: v2 AddressLookup renders for everyone. The legacy component is
 * available as a ~24h post-deploy rollback escape hatch only — do NOT design
 * any new feature against the legacy component.
 */
export const ADDRESS_PICKER_V2_QUERY_PARAM = 'address-picker';
export const ADDRESS_PICKER_V2_LOCAL_STORAGE_KEY = 'address_picker_v2';

/**
 * Returns true only when the user has explicitly opted into the legacy
 * AddressLookup. Default is false (v2 renders).
 */
export function shouldRenderLegacyAddressLookup(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  // 1. URL query param — wins, and persists to localStorage so the override
  //    survives a route change off the home page (where the picker is not
  //    rendered) onto Collection-Delivery / Removals (where it is).
  try {
    const params = new URLSearchParams(window.location.search);
    const flagValue = params.get(ADDRESS_PICKER_V2_QUERY_PARAM);
    if (flagValue !== null) {
      if (flagValue === 'legacy') {
        try {
          window.localStorage.setItem(
            ADDRESS_PICKER_V2_LOCAL_STORAGE_KEY,
            '0',
          );
        } catch {
          /* noop — quota exceeded, private mode, etc. */
        }
        return true;
      }
      // Any non-"legacy" value (including "v2") clears the rollback flag so
      // the user re-rejoins the v2 default. No-op when nothing was set.
      try {
        window.localStorage.setItem(
          ADDRESS_PICKER_V2_LOCAL_STORAGE_KEY,
          '1',
        );
      } catch {
        /* noop */
      }
      return false;
    }
  } catch {
    /* noop — URL parsing failed in some exotic environment. */
  }

  // 2. localStorage fallback. Only "0" opts the user OUT of v2.
  try {
    return (
      window.localStorage.getItem(ADDRESS_PICKER_V2_LOCAL_STORAGE_KEY) === '0'
    );
  } catch {
    return false;
  }
}
